import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TelegramLinkCard from "../components/TelegramLinkCard";
import ClientRubricCard from "../components/ClientRubricCard";
import UploadLinkCard from "../components/UploadLinkCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  User,
  Store,
  CalendarDays,
  Check,
  X,
  Crown,
  FolderOpen,
  RefreshCw,
  Gift,
  Save,
} from "lucide-react";
import { Input } from "../components/ui/input";

const statusMap = {
  pending: { label: "In attesa", cls: "bg-[var(--mp-yellow)] text-[var(--mp-ink)]" },
  approved: { label: "Approvato", cls: "bg-[var(--mp-ink)] text-white" },
  rejected: { label: "Rifiutato", cls: "bg-[var(--mp-primary)] text-white" },
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: selfUser } = useAuth();
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [openingDropbox, setOpeningDropbox] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/clients/${id}`);
      setClient(data.user);
      setProfile(data.profile || null);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/dashboard/clienti");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (action) => {
    setWorking(true);
    try {
      const { data } = await api.post(`/staff/client-requests/${id}`, { action });
      setClient(data.user);
      toast.success(action === "approve" ? "Cliente approvato" : "Cliente rifiutato");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  const openDropboxFolder = async () => {
    setOpeningDropbox(true);
    // Open a tab synchronously to avoid mobile/Safari popup blocking
    const newTab = window.open("about:blank", "_blank");
    try {
      const { data } = await api.get(`/staff/clients/${id}/dropbox-folder`);
      if (data?.url) {
        if (newTab) {
          newTab.location.href = data.url;
        } else {
          window.location.href = data.url;
        }
      } else {
        if (newTab) newTab.close();
        toast.error("Link Dropbox non disponibile");
      }
    } catch (e) {
      if (newTab) newTab.close();
      toast.error(apiError(e));
    } finally {
      setOpeningDropbox(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="client-detail-loading" className="text-[var(--mp-muted)]">
        Caricamento scheda…
      </div>
    );
  }
  if (!client) return null;

  const statusInfo = statusMap[client.status] || { label: client.status, cls: "" };
  const initials = `${client.referente_nome?.[0] || ""}${
    client.referente_cognome?.[0] || ""
  }`.toUpperCase();

  return (
    <div data-testid="client-detail-page" className="space-y-8 fade-up">
      <div>
        <button
          onClick={() => navigate("/dashboard/clienti")}
          data-testid="client-detail-back"
          className="inline-flex items-center gap-2 text-sm text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
        >
          <ArrowLeft className="h-4 w-4" /> Tutti i clienti
        </button>
      </div>

      {/* Header card */}
      <div className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-8 sm:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
          <div
            data-testid="client-detail-avatar"
            className="h-20 w-20 rounded-2xl bg-[var(--mp-yellow)] grid place-items-center font-display text-2xl tracking-tight border border-[var(--mp-ink)]"
          >
            {initials || "SL"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
              Scheda cliente · Salone
            </div>
            <h1
              data-testid="client-detail-name"
              className="font-display text-4xl sm:text-5xl tracking-tighter mt-1.5 leading-[1.05]"
            >
              {client.name_salone}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Badge
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${statusInfo.cls}`}
              >
                {statusInfo.label}
              </Badge>
              {client.plan === "pro" && (
                <Badge
                  data-testid="client-plan-badge"
                  className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] bg-[var(--mp-yellow)] text-[var(--mp-ink)]"
                >
                  <Crown className="h-3 w-3 mr-1 inline" /> Pro
                </Badge>
              )}
              <span className="text-sm text-[var(--mp-muted)]">
                {client.referente_nome} {client.referente_cognome}
              </span>
            </div>
          </div>

          {client.status === "pending" ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid="client-detail-dropbox"
                disabled={openingDropbox}
                onClick={openDropboxFolder}
                variant="outline"
                className="rounded-full border-[var(--mp-ink)]"
                title="Apre la cartella Dropbox del cliente in una nuova scheda"
              >
                <FolderOpen className="h-4 w-4 mr-1" />
                {openingDropbox ? "Apro…" : "Cartella Dropbox"}
              </Button>
              <Button
                data-testid="client-detail-approve"
                disabled={working}
                onClick={() => act("approve")}
                className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
              >
                <Check className="h-4 w-4 mr-1" /> Approva
              </Button>
              <Button
                variant="outline"
                data-testid="client-detail-reject"
                disabled={working}
                onClick={() => act("reject")}
                className="rounded-full border-[var(--mp-ink)]"
              >
                <X className="h-4 w-4 mr-1" /> Rifiuta
              </Button>
            </div>
          ) : (
            <Button
              data-testid="client-detail-dropbox"
              disabled={openingDropbox}
              onClick={openDropboxFolder}
              variant="outline"
              className="rounded-full border-[var(--mp-ink)]"
              title="Apre la cartella Dropbox del cliente in una nuova scheda"
            >
              <FolderOpen className="h-4 w-4 mr-1" />
              {openingDropbox ? "Apro…" : "Cartella Dropbox"}
            </Button>
          )}
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <InfoTile
            icon={<Store className="h-4 w-4" />}
            label="Nome salone"
            value={client.name_salone}
            testId="client-info-salon"
          />
          <InfoTile
            icon={<User className="h-4 w-4" />}
            label="Referente"
            value={`${client.referente_nome} ${client.referente_cognome}`}
            testId="client-info-ref"
          />
          <InfoTile
            icon={<Mail className="h-4 w-4" />}
            label="Email"
            value={client.email}
            testId="client-info-email"
          />
          <InfoTile
            icon={<CalendarDays className="h-4 w-4" />}
            label="Registrato il"
            value={
              client.created_at
                ? new Date(client.created_at).toLocaleString("it-IT")
                : "—"
            }
            testId="client-info-date"
          />
        </div>
      </div>

      {/* Admin-only: plan toggle */}
      {selfUser?.role === "admin" && (
        <PlanToggleCard
          client={client}
          onChange={(u) => setClient(u)}
        />
      )}

      {/* Admin-only: reset AI quotas (cover/day + polish positioning one-shot) */}
      {selfUser?.role === "admin" && (
        <ResetAiQuotaCard clientId={client.id} />
      )}

      {/* Admin-only: offer generator quota + reset + per-client override */}
      {selfUser?.role === "admin" && (
        <OfferQuotaAdminCard clientId={client.id} />
      )}

      {/* Fixed personal upload link: copy / send via Telegram / regenerate */}
      <UploadLinkCard
        clientId={client.id}
        telegramLinked={Boolean(client.telegram_linked)}
      />

      {/* Rubrica cliente: anagrafica, posizionamento, link, note riservate */}
      <ClientRubricCard
        clientId={client.id}
        initialProfile={profile}
        initialClient={client}
        onClientUpdate={(u) => setClient((prev) => ({ ...prev, ...u }))}
        onProfileUpdate={(p) => setProfile(p)}
      />

      {/* Telegram link status */}
      <TelegramLinkCard client={client} onChange={load} />
    </div>
  );
}

function InfoTile({ icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-4"
    >
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
        {icon} {label}
      </div>
      <div className="mt-2 font-display text-base tracking-tight truncate">{value || "—"}</div>
    </div>
  );
}

function PlanToggleCard({ client, onChange }) {
  const [working, setWorking] = useState(false);
  const isPro = client.plan === "pro";

  const togglePlan = async () => {
    const next = isPro ? "free" : "pro";
    setWorking(true);
    try {
      const { data } = await api.patch(`/admin/clients/${client.id}/plan`, {
        plan: next,
      });
      onChange(data.user);
      toast.success(
        next === "pro" ? "Piano Pro attivato" : "Piano Pro disattivato"
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="client-plan-card"
      className={`rounded-3xl border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 ${
        isPro
          ? "border-[var(--mp-ink)] bg-[var(--mp-yellow)]"
          : "border-[var(--mp-border)] bg-white"
      }`}
    >
      <div
        className={`h-12 w-12 rounded-2xl grid place-items-center shrink-0 ${
          isPro ? "bg-[var(--mp-ink)] text-white" : "bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]"
        }`}
      >
        <Crown className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          Piano del cliente
        </div>
        <div className="font-display text-xl tracking-tight mt-1">
          {isPro ? "Pro — servizi premium attivi" : "Free — servizi base"}
        </div>
        <div className="text-sm text-[var(--mp-muted)] mt-1">
          {isPro
            ? "Riceve ogni mattina alle 9:00 le 3 idee storie AI su Telegram."
            : "Nessun servizio premium attivo. Attiva Pro per abilitare le idee storie giornaliere."}
        </div>
      </div>
      <Button
        data-testid="client-plan-toggle"
        disabled={working}
        onClick={togglePlan}
        className={
          isPro
            ? "rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            : "rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
        }
      >
        {working ? "…" : isPro ? "Disattiva Pro" : "Attiva Pro"}
      </Button>
    </div>
  );
}

function ResetAiQuotaCard({ clientId }) {
  const [working, setWorking] = useState(false);

  const resetQuota = async () => {
    if (
      !window.confirm(
        "Confermi di voler resettare le quote AI di questo cliente?\n\n" +
          "• Potrà generare di nuovo 1 copertina oggi\n" +
          "• Potrà ri-eseguire il \"Migliora con AI\" sul posizionamento"
      )
    ) {
      return;
    }
    setWorking(true);
    try {
      await api.post(`/admin/clients/${clientId}/reset-polish-quota`);
      toast.success("Quote AI resettate. Il cliente può rigenerare ora.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="client-reset-ai-quota-card"
      className="rounded-3xl border border-[var(--mp-border)] bg-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0 bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]">
        <RefreshCw className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
          Strumenti AI · quote cliente
        </div>
        <div className="font-display text-xl tracking-tight mt-1">
          Reset quote AI self-service
        </div>
        <div className="text-sm text-[var(--mp-muted)] mt-1">
          Sblocca al cliente una nuova generazione copertina (oggi) e ripristina
          la possibilità di rilanciare "Migliora con AI" sul posizionamento.
          Utile per supporto o per ri-test.
        </div>
      </div>
      <Button
        data-testid="client-reset-ai-quota-btn"
        disabled={working}
        onClick={resetQuota}
        variant="outline"
        className="rounded-full border-[var(--mp-ink)]"
      >
        <RefreshCw className="h-4 w-4 mr-1" />
        {working ? "Reset…" : "Resetta quote AI"}
      </Button>
    </div>
  );
}

function OfferQuotaAdminCard({ clientId }) {
  const [state, setState] = useState(null); // {plan, plan_default, override, effective_quota, cooldown}
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideInput, setOverrideInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/offer/client/${clientId}`);
      setState(data);
      setOverrideInput(data.override != null ? String(data.override) : "");
    } catch (e) {
      // Non-blocking
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const doReset = async () => {
    if (!window.confirm(
      "Azzero il contatore delle offerte di questa settimana per questo cliente. " +
      "Potrà generare subito una nuova offerta. Confermi?"
    )) return;
    setResetting(true);
    try {
      await api.post(`/admin/offer/reset/${clientId}`);
      toast.success("Contatore azzerato. Il cliente può generare ora.");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  const saveOverride = async () => {
    const trimmed = overrideInput.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 1)) {
      toast.error("Inserisci un numero >= 1 oppure lascia vuoto per usare il default");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/offer/client/${clientId}`, { max_per_week: value });
      toast.success(
        value === null
          ? "Override rimosso — torna al default del piano"
          : `Quota override impostata: ${value}/settimana`
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="client-offer-quota-card"
      className="rounded-3xl border border-[var(--mp-border)] bg-white p-5 sm:p-6 space-y-4"
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0 bg-[var(--mp-yellow)] border border-[var(--mp-ink)] text-[var(--mp-ink)]">
          <Gift className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Creatore Offerte · quota cliente
          </div>
          <div className="font-display text-xl tracking-tight mt-1">
            Offerte AI di questo cliente
          </div>
          {loading ? (
            <div className="text-sm text-[var(--mp-muted)] mt-1">Carico…</div>
          ) : state ? (
            <div className="text-sm text-[var(--mp-muted)] mt-1">
              Piano <strong className="text-[var(--mp-ink)]">{state.plan}</strong>{" "}
              · quota effettiva{" "}
              <strong className="text-[var(--mp-ink)]">
                {state.effective_quota}/settimana
              </strong>{" "}
              (default piano: {state.plan_default}
              {state.override != null ? `, override: ${state.override}` : ""})
              · usate{" "}
              <strong className="text-[var(--mp-ink)]">
                {state.cooldown?.used ?? 0}
              </strong>{" "}
              di {state.cooldown?.max ?? state.effective_quota} questa settimana
              {state.cooldown?.next_available_at && !state.cooldown?.available && (
                <>
                  {" "}· prossima disponibile{" "}
                  {new Date(state.cooldown.next_available_at).toLocaleString("it-IT")}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 pt-1">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1">
            Override quota (vuoto = usa default piano)
          </div>
          <div className="inline-flex items-center gap-2">
            <Input
              data-testid="client-offer-override-input"
              type="number"
              min="1"
              max="50"
              step="1"
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              placeholder={String(state?.plan_default ?? "")}
              className="w-24 rounded-full border-[var(--mp-ink)] h-9"
            />
            <Button
              data-testid="client-offer-override-save"
              onClick={saveOverride}
              disabled={saving || loading}
              size="sm"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? "Salvo…" : "Salva"}
            </Button>
          </div>
        </div>

        <Button
          data-testid="client-offer-reset-btn"
          onClick={doReset}
          disabled={resetting || loading}
          variant="outline"
          className="rounded-full border-[var(--mp-ink)]"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          {resetting ? "Azzero…" : "Azzera contatore settimana"}
        </Button>
      </div>
    </div>
  );
}

